/* eslint-disable @typescript-eslint/no-explicit-any */
import { PortableText, PortableTextProps } from '@portabletext/react'
import { SanityImageSource } from '@sanity/image-url/lib/types/types'
import {
  Image,
  OrContentBlockC,
  OrContentBlockH,
  OrZoSection,
  OrFlexibleBlock,
  OrMapTile,
  Tab,
  FlexibleBlockContent,
  Media,
} from '@studio/atomic-ui'
import { googleMapsApiKey } from '../../env'
import { BuildingDetailTemplate } from '.'
import { FilterBlock } from './filter-block'
import { mapImage } from '../../services/sanity/client'
import { addBasePath } from '../../utils/helpers'
import { mapButton, mapLink } from '../../services/sanity/mappers'
import { SanityAtButton } from '../../services/sanity/types'
import { trackContentCardClick, trackTabClick } from '../../utils/analytics'
import { ObserverRefsType } from '../../types/observerRef'
import { ProductType } from '../../types/product'

export interface CardImage {
  alt: string
  asset: {
    _ref: string
    _type: string
  }
  _type: string
  caption: string
}

export interface Card {
  title: string
  description: string
  additionalInfo: {
    info1: string
    info2: string
  }
  images: {
    images: CardImage[]
  }
}

export interface ContentBlock {
  layout: string
  name: string
  tabsTitle: string
  outline: string
  outline2: string
  fieldName: string
  handwrittenDetail: string
  sectionTitle: string
  sectionTitle2?: string
  sectionSubtitle: string
  sectionDescription?: string
  sectionDescription2?: string
  sectionFooter?: string
  sectionBody: PortableTextProps['value']
  images: Array<{ alt: string; asset: SanityImageSource }>
  logo?: {
    alt: string
    asset: {
      _ref: string
    }
  }
  button: SanityAtButton
  cards: Card[]
  tabs: any
  filterType: ProductType
  filterTypes?: ProductType[]
  filterBuilding?: string
  filterRegion?: string
  filterCapacity?: string
  baseRoute: string
}

interface InfoFlexibleBlock {
  title: string
  tabs: (Omit<Tab, 'content'> & {
    content: FlexibleBlockContent
  })[]
}

export type ContentBlockProps = Omit<BuildingDetailTemplate, 'content'> & {
  content: ContentBlock
  observerRefs: ObserverRefsType
}

export const ContentBlock = (props: ContentBlockProps) => {
  const images: Media[] = props.content?.images?.map((image) => {
    return mapImage(image, { width: 607 })
  })
  const isLogoImage = props.content.logo !== null && mapImage(props.content.logo, { width: 482 })

  let setInfoContentBlockH = null
  if (props.content.layout === 'cards') {
    setInfoContentBlockH = {
      title: props.content.sectionTitle,
      text: props.content.sectionDescription,
      content: props.content.cards.map((card: Card) => {
        return {
          title: card.title,
          description: card.description,
          price: card.additionalInfo.info1,
          floor: card.additionalInfo.info2,
          images: card.images.images.map((image: CardImage) => {
            const mapImages = mapImage(image, { width: 607 })
            return {
              src: mapImages.src,
              alt: image.alt || `${card.title} workspace illustration`,
            }
          }),
        }
      }),
    }
  }
  let setinfoFlexibleBlock: InfoFlexibleBlock | null = null
  if (props.content.fieldName === 'selectedTab') {
    setinfoFlexibleBlock = {
      title: props.content.tabsTitle,
      tabs: props.content?.tabs?.map((tab: any) => {
        return {
          label: tab.label,
          content: {
            type: 'text',
            images: tab?.content[0]?.images?.map((image: any) => mapImage(image, { width: 607 })),
            title: tab.content[0].contentTitle,
            body: tab.content[0].detailsBody,
            details:
              tab?.content[0]?.details?.items.map((item: any) => {
                return {
                  label: item.body,
                  icon: item.icon?.src,
                  link: item.link ? mapLink(item.link) : null,
                }
              }) || [],
          },
        }
      }),
    }
  }

  switch (props.content.layout || props.content.fieldName) {
    case 'longText':
      return <FilterBlock images={images} {...props} />

    case 'cards':
      return (
        <div
          data-testid="cards"
          className="mx-auto w-full lg:max-w-screen-lg px-4 sm:px-8 slg:px-12 py-4 lg:pb-16 lg:pt-12 lg:px-[10.4375rem]"
        >
          <hr className="block border mt-10 border-neutrals-400" />
          <div
            className="mt-24"
            id="workspace-options"
            ref={(el) => {
              props.observerRefs.current['workspace-options'] = el
            }}
          >
            <OrContentBlockH
              title={setInfoContentBlockH?.title || ''}
              text={setInfoContentBlockH?.text || ''}
              content={setInfoContentBlockH?.content || []}
            />
          </div>

          <hr className="block border mt-16 border-neutrals-400" />

          {props.address && (
            <section
              id="getting-here"
              className="mt-16 mb-12 scroll-mt-24"
              ref={(el) => {
                props.observerRefs.current['getting-here'] = el
              }}
            >
              <PortableText
                value={props.address.heading}
                components={{
                  block: {
                    h2: ({ children }) => (
                      <h2 className="font-heading font-medium text-32 leading-1.3 pb-4">{children}</h2>
                    ),
                  },
                }}
              />

              <div className="flex flex-col gap-4 md:gap-12 md:flex-row mt-4 mb-10">
                <PortableText
                  value={props.address.addressInfo}
                  components={{
                    block: {
                      normal: ({ children }) => <p className="text-neutrals-800 text-16 lg:text-16">{children}</p>,
                      strong: ({ children }) => (
                        <strong className="only:inline-block first:inline-block first:mt-6 only:mt-6 font-bold text-neutrals-800 leading-1.5 text-16">
                          {children}
                        </strong>
                      ),
                    },
                  }}
                />
              </div>

              {props.address.mapTile && (
                <OrMapTile
                  apiKey={googleMapsApiKey}
                  markers={[{ address: props.address.mapTile.address }]}
                  details={props.address.mapTile.details.items.map((item: { icon: { src: any }; title: any }) => ({
                    icon: item.icon?.src,
                    label: item.title,
                  }))}
                />
              )}
            </section>
          )}
        </div>
      )

    case 'zo':
      return (
        // VQA REF: studio-content-block-mobile
        // TODO: remove when QA has completed their pass
        <div className="bg-zocolors-background py-12 w-full lg:w-auto px-4 sm:px-8 slg:px-12 lg:px-0">
          <OrZoSection
            title={props.content.sectionTitle}
            subTitle={props.content.sectionDescription || ''}
            label={props.content.button.label}
            backgroundImage={images[0] as Image}
            logo={isLogoImage || { alt: '', src: '' }}
            onClick={() =>
              window.open(addBasePath(props.content.button.actionValue ?? ''), props.content.button.target ?? '')
            }
            onTrackClick={() =>
              trackContentCardClick({
                contentHeadline: props.content.sectionTitle,
                linkUrl: addBasePath(props.content.button.actionValue ?? ''),
                componentName: 'zo section',
                windowLocationHref: window.location.href,
              })
            }
          />
        </div>
      )

    case 'finalAddendum':
      return (
        //VQA REF: studio-content-block-c
        // TODO: remove when QA has completed their pass
        <div className="py-8 lg:pt-16 lg:pb-16 max-w-[1440px] mx-auto" id="neighborhood">
          <OrContentBlockC
            mainText={props.content.outline}
            title={props.content.sectionTitle}
            subTitle={props.content.sectionSubtitle || ''}
            body={props.content.sectionDescription || ''}
            signature={props.content.handwrittenDetail}
            media={images[0] as Media}
            mainTextTwo={props.content.outline2}
            titleTwo={props.content.sectionTitle2 || ''}
            bodyTwo={props.content.sectionDescription2 || ''}
            buttonText={props.content.outline2 || ''}
            button={mapButton(props.content.button)}
            mediaTwo={images[1] as Media}
            onClick={() =>
              window.open(addBasePath(props.content.button.actionValue ?? ''), props.content.button.target ?? '')
            }
            onTrackClick={() =>
              trackContentCardClick({
                contentHeadline: props.content.sectionTitle2 || '',
                linkUrl: addBasePath(props.content.button.actionValue ?? ''),
                componentName: 'content block c',
                windowLocationHref: window.location.href,
              })
            }
          />
        </div>
      )

    case 'selectedTab':
      return (
        <div
          id="building-features"
          ref={(el) => {
            props.observerRefs.current['building-features'] = el
          }}
        >
          <OrFlexibleBlock
            title={setinfoFlexibleBlock?.title || ''}
            tabs={setinfoFlexibleBlock?.tabs || []}
            onChange={(index: number) =>
              trackTabClick({
                titleDescriptor: setinfoFlexibleBlock?.tabs[index].label,
                componentName: 'tm-building-detail',
                windowLocationHref: window.location.href,
              })
            }
          />
        </div>
      )

    default:
      return null
  }
}
